import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Points, Vector3 } from 'three';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { CloudFrontSigner } from '../../../../utils/cloud-front-signer/cloud-front-signer';
import { isDefined } from '../../../../utils/variables';
import { GeodataContext } from '../geodata-state';
import { GeodataSpatialReferenceContext } from './geodata-spatial-reference-state';
import { classicDataLoaders } from './sfm-data-loaders/classic-data-loaders';
import { odmDataLoaders } from './sfm-data-loaders/odm-data-loaders';

export const useSparseReconstruction = () => {
  const { origo } = useContext(GeodataSpatialReferenceContext);
  const { geodata, signer } = useContext(GeodataContext);
  const [sparsePointCloud, setSparsePointCloud] = useState<Points>();
  const getSparsePointCloud = useGetPointSparseCloudMutation(setSparsePointCloud);

  const sparsePointCloudUrl = useMemo(
    () => geodata.resourceUrls?.find((x) => x.name === 'sparse_point_cloud')?.url,
    [geodata],
  );

  useEffect(() => {
    if (isDefined(origo) && isDefined(sparsePointCloudUrl) && isDefined(signer)) {
      getSparsePointCloud({ geodata, sparsePointCloudUrl, signer, origo });
    }
  }, [geodata, sparsePointCloudUrl, signer, origo, getSparsePointCloud]);

  return { sparsePointCloud };
};

type GetSparsePointCloudParams = {
  geodata: GeodataModel;
  signer: CloudFrontSigner;
  sparsePointCloudUrl: string;
  origo: Vector3;
};

async function getSparsePointCloud(params: GetSparsePointCloudParams): Promise<Points> {
  if (params.geodata.processType === 'ODM') {
    return await odmDataLoaders.loadSparsePointCloud(params.signer, params.origo);
  }

  return await classicDataLoaders.loadSparsePointCloud(params.sparsePointCloudUrl, params.origo);
}

/**
 * Fetches gcp points, cameras and sparse point cloud asynchronously
 * based on the geodata.processType.
 */
function useGetPointSparseCloudMutation(setSparsePointCloud: (value: Points) => void) {
  const mutation = useMutation({
    mutationFn: (params: GetSparsePointCloudParams) => {
      return getSparsePointCloud(params);
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (data) => {
      setSparsePointCloud(data);
    },
  });

  return mutation.mutate;
}
